/**
 * ConnectorRow — one connection as [name + ring] [sensor] [green toggle].
 *
 * The toggle carries operator INTENT (enabled) only; the sensor carries
 * STATE. They are deliberately separate signals: a connector can be ON and
 * broken, or ON and not yet connected, and the row must be able to say so
 * (UX review 2026-08-12). Sensor rules:
 *   - text label always accompanies the dot — color is never the only channel
 *   - steady when healthy; pulse ONLY for attention states (expired/degraded)
 *     and while a live test is in flight; prefers-reduced-motion stills it
 *   - the "checked Xm ago" sub admits staleness instead of faking liveness
 *
 * Unhealthy rows auto-expand to the troubleshoot body (reconnect / rotate
 * key / test / error detail) because the toggle's affordance is never the
 * fix for a red sensor. A manual collapse wins over auto-expand.
 */

import React from 'react';
import {
  AlertTriangle, ChevronDown, KeyRound, Loader2, PlayCircle,
} from 'lucide-react';

// healthScheduler.js AUTO_DISABLE_AFTER — a row disabled with this many
// consecutive failures was almost certainly paused by the scheduler, not a
// human, and must not read as a plain "Off" (the toggle-that-lies trap).
const AUTO_DISABLE_AFTER = 10;

export function timeAgo(iso) {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Collapse enabled × credentials × health into one honest sensor state. */
export function sensorFor(connector, testing) {
  if (testing) {
    return { tone: 'wait', label: 'Checking…', sub: null, pulse: true };
  }
  const checked = timeAgo(connector.lastCheckAt);
  const checkedSub = checked ? `checked ${checked}` : 'never checked';
  if (!connector.enabled) {
    if ((connector.consecutiveFailures || 0) >= AUTO_DISABLE_AFTER) {
      return {
        tone: 'err',
        label: 'Auto-paused',
        sub: `${connector.consecutiveFailures} straight failures`,
        pulse: false,
      };
    }
    return { tone: 'off', label: 'Off', sub: null, pulse: false };
  }
  if (connector.credentialsPresent === false) {
    return { tone: 'off', label: 'Not connected', sub: null, pulse: false };
  }
  switch (connector.health) {
    case 'healthy':
      return { tone: 'ok', label: 'Connected', sub: checkedSub, pulse: false };
    case 'degraded':
      return { tone: 'warn', label: 'Degraded', sub: checkedSub, pulse: true };
    case 'expired':
      return { tone: 'warn', label: 'Expired — reconnect', sub: checkedSub, pulse: true };
    case 'failed':
      return { tone: 'err', label: 'Failed', sub: checkedSub, pulse: false };
    default:
      return { tone: 'off', label: 'Not checked', sub: null, pulse: false };
  }
}

/** True when the row should open its troubleshoot body on its own. */
export function needsAttention(connector) {
  if (!connector.enabled) {
    return (connector.consecutiveFailures || 0) >= AUTO_DISABLE_AFTER;
  }
  return (
    ['failed', 'expired', 'degraded'].includes(connector.health)
    || connector.credentialsPresent === false
  );
}

function ConnectorRow({
  connector, expanded, flash, rowRef,
  onToggleExpand, onToggle, onTest, testing,
  onConnect, onGrant, onSaveKey, saving, keyError,
  onDisconnect, disconnecting, extraActions = [], bodyExtra = null,
}) {
  const [keyOpen, setKeyOpen] = React.useState(false);
  const [keyValue, setKeyValue] = React.useState('');
  const sensor = sensorFor(connector, testing);
  const lastSuccess = timeAgo(connector.lastSuccessAt);

  const submitKey = () => {
    if (!keyValue.trim() || saving) return;
    onSaveKey(connector.key, keyValue.trim(), () => { setKeyOpen(false); setKeyValue(''); });
  };

  return (
    <div
      ref={rowRef}
      className={`cxr cxr--${sensor.tone}${expanded ? ' cxr--open' : ''}${flash ? ' cxr--flash' : ''}`}
    >
      <div className="cxr-head">
        <button
          type="button"
          className="cxr-main"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          title={expanded ? 'Hide details' : 'Show details'}
        >
          <ChevronDown size={14} className="cxr-chevron" aria-hidden="true" />
          <span className="cxr-name">{connector.displayName}</span>
          <span className={`cxr-ringchip ${connector.ring === 'panw' ? 'cxr-ringchip--panw' : ''}`}>
            {connector.ring === 'panw' ? 'PANW ring' : 'personal'}
          </span>
        </button>

        <span className={`cxr-sensor cxr-sensor--${sensor.tone}`} role="status">
          {sensor.tone === 'wait'
            ? <Loader2 size={10} className="cxr-spin" aria-hidden="true" />
            : <span className={`cxr-dot${sensor.pulse ? ' cxr-dot--pulse' : ''}`} aria-hidden="true" />}
          <span className="cxr-sensor-label">{sensor.label}</span>
          {sensor.sub && <span className="cxr-sensor-sub">{sensor.sub}</span>}
        </span>

        <label className="cxr-switch">
          <input
            type="checkbox"
            role="switch"
            checked={connector.enabled}
            aria-checked={connector.enabled}
            aria-label={`${connector.displayName} enabled`}
            onChange={() => onToggle(connector)}
          />
          <span className="cxr-slider" aria-hidden="true" />
        </label>
      </div>

      {expanded && (
        <div className="cxr-body">
          {connector.description && <p className="cxr-desc">{connector.description}</p>}

          <div className="cxr-meta">
            <span className="cxr-meta-item">
              <KeyRound size={12} aria-hidden="true" />
              {connector.authType}
              {connector.credentialsPresent === false && ' — no credentials'}
            </span>
            <span className="cxr-meta-item">
              Last success: {lastSuccess || 'never'}
            </span>
          </div>

          {!connector.enabled && (connector.consecutiveFailures || 0) >= AUTO_DISABLE_AFTER && (
            <div className="cxr-error">
              <AlertTriangle size={12} aria-hidden="true" />
              Paused automatically after {connector.consecutiveFailures} consecutive
              failures — fix the credentials below, test, then switch it back on.
            </div>
          )}

          {connector.lastError && connector.health !== 'healthy' && (
            <div className="cxr-error">
              <AlertTriangle size={12} aria-hidden="true" />
              {connector.lastError}
            </div>
          )}

          {connector.credentialsPresent === false && connector.authHelp && (
            <div className="cxr-hint">{connector.authHelp}</div>
          )}

          {connector.grants?.length > 0 && connector.credentialsPresent && (
            <div className="cxr-grants">
              {connector.grants.map((g) => (
                <button
                  key={g.name}
                  type="button"
                  className={`cxr-grant ${g.granted ? 'cxr-grant--on' : ''}`}
                  title={g.granted ? `${g.name} access granted` : `Add ${g.name} access (one-click consent)`}
                  onClick={() => !g.granted && onGrant(connector.key, g.name)}
                  disabled={g.granted}
                >
                  {g.granted ? '✓ ' : '+ '}{g.name}
                </button>
              ))}
            </div>
          )}

          {bodyExtra}

          {keyOpen && (
            <div className="cxr-keyform">
              <input
                type="password"
                className="cxr-key-input"
                placeholder="Paste API key — validated live before saving"
                value={keyValue}
                autoComplete="new-password"
                onChange={(e) => setKeyValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitKey();
                  if (e.key === 'Escape') {
                    // Close the form, not the whole Settings overlay.
                    e.stopPropagation();
                    setKeyOpen(false);
                    setKeyValue('');
                  }
                }}
                autoFocus
              />
              <button
                type="button"
                className="cxr-btn cxr-btn--primary"
                disabled={!keyValue.trim() || saving}
                onClick={submitKey}
              >
                {saving ? <Loader2 size={14} className="cxr-spin" /> : 'Save'}
              </button>
              {keyError && !saving && (
                <span className="cxr-key-error">{keyError}</span>
              )}
            </div>
          )}

          <div className="cxr-actions">
            {connector.canConnect && (
              <button type="button" className="cxr-btn cxr-btn--primary" onClick={() => onConnect(connector.key)}>
                {connector.credentialsPresent ? 'Reconnect' : 'Connect'}
              </button>
            )}
            {connector.authType === 'api_key' && (
              <button type="button" className="cxr-btn" onClick={() => setKeyOpen((v) => !v)}>
                <KeyRound size={14} aria-hidden="true" />
                {connector.vaultCredentials?.length ? 'Rotate key' : 'Set key'}
              </button>
            )}
            <button type="button" className="cxr-btn" onClick={() => onTest(connector.key)} disabled={testing}>
              {testing ? <Loader2 size={14} className="cxr-spin" /> : <PlayCircle size={14} aria-hidden="true" />}
              Test
            </button>
            {extraActions.map((action) => (
              <button
                key={action.label}
                type="button"
                className="cxr-btn"
                onClick={action.onClick}
                disabled={action.pending}
              >
                {action.pending && <Loader2 size={14} className="cxr-spin" />}
                {action.label}
              </button>
            ))}
            {onDisconnect && connector.credentialsPresent && (
              <button
                type="button"
                className="cxr-btn cxr-btn--danger"
                onClick={() => onDisconnect(connector)}
                disabled={disconnecting}
                title="Delete stored credentials — the row reads Not connected until you reconnect"
              >
                {disconnecting ? <Loader2 size={14} className="cxr-spin" /> : 'Disconnect'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ConnectorRow;
